import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdvertisementServices } from '../../apis/advertisementService';
import { toast } from 'react-toastify';
import { Save, X, FileUp, Trash2, ArrowLeft, Image } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import PageFlipViewer from '../../components/PageFlipViewer';
import PublishingOptionsPanel from '../../components/admin-content-editor/PublishingOptionsPanel';

const AddAdvertisementAdminPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    isFeatured: false,
    isActive: true
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  const { createAdvertisement, getAdvertisementById, updateAdvertisement, loading, error } = useAdvertisementServices();

  // Fetch advertisement data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      fetchAdvertisementData();
    }
  }, [id]);

  // Show API errors
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const fetchAdvertisementData = async () => {
    try {
      const response = await getAdvertisementById(id);
      if (response.success) {
        const advertisementData = response.data;
        setFormData({
          title: advertisementData.title,
          content: advertisementData.content,
          category: advertisementData.category,
          isFeatured: advertisementData.isFeatured,
          isActive: advertisementData.isActive
        });
        
        if (advertisementData.pdfUrl) {
          setPdfPreview(advertisementData.pdfUrl);
        }
        
        if (advertisementData.imageUrl) {
          setImagePreview(advertisementData.imageUrl);
        }
      } else {
        toast.error('Failed to fetch advertisement data');
        navigate('/super-admin/advertisement-list');
      }
    } catch (err) {
      console.error('Error fetching advertisement data:', err);
      toast.error('An error occurred while fetching advertisement data');
      navigate('/super-admin/advertisement-list');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
    if (errors.content) {
      setErrors(prev => ({ ...prev, content: '' }));
    }
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Only PDF files are allowed');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('PDF file size should be less than 5MB');
        return;
      }
      
      setPdfFile(file);
      
      // Create a preview URL for the PDF
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPdfPreview(URL.createObjectURL(file));
      };
      fileReader.readAsArrayBuffer(file);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }
      
      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image file size should be less than 2MB');
        return;
      }
      
      setImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePdf = () => {
    setPdfFile(null);
    setPdfPreview('');
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }
    
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let response;
      
      if (isEditMode) {
        response = await updateAdvertisement(id, formData, pdfFile, imageFile);
      } else {
        response = await createAdvertisement(formData, pdfFile, imageFile);
      }
      
      if (response.success) {
        toast.success(`Advertisement ${isEditMode ? 'updated' : 'created'} successfully`);
        navigate('/super-admin/advertisement-list');
      } else {
        toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} advertisement`);
      }
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} advertisement:`, err);
      toast.error(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the advertisement`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image', 'align'
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/super-admin/advertisement-list')}
          className="mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditMode ? 'Edit Advertisement' : 'Add New Advertisement'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,17.5rem] xl:grid-cols-[1fr,19rem] gap-6 lg:gap-8 items-start">
          <div className="min-w-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Title */}
          <div className="col-span-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full p-2 border ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-green-500`}
              placeholder="Enter advertisement title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          {/* Category */}
          <div className="md:col-span-2">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className={`w-full p-2 border ${
                errors.category ? 'border-red-500' : 'border-gray-300'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-green-500`}
            >
              <option value="">Select a category</option>
              <option value="Promotion">Promotion</option>
              <option value="Announcement">Announcement</option>
              <option value="Event">Event</option>
              <option value="Product">Product</option>
              <option value="Service">Service</option>
              <option value="General">General</option>
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-500">{errors.category}</p>
            )}
          </div>

          {/* Image Upload */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Advertisement Image
            </label>
            <div className="mt-1">
              {imagePreview ? (
                <div className="flex items-start space-x-4">
                  <div className="relative">
                    <div className="w-32 h-32 bg-gray-200 rounded-lg border border-gray-300 flex items-center justify-center">
                      <img
                        src={imagePreview}
                        alt="Advertisement preview"
                        className="w-full h-full object-contain rounded-lg"
                        style={{
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '100%'
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-2">
                      {imageFile ? imageFile.name : 'Current image'}
                    </p>
                    <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                      <Image size={16} className="mr-2" />
                      Change Image
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="flex items-center">
                  <label className="flex flex-col items-center px-4 py-2 bg-white text-blue-600 rounded-lg border border-blue-600 cursor-pointer hover:bg-blue-50">
                    <Image size={18} className="mb-1" />
                    <span className="text-sm">Upload Image</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>
                  <span className="ml-3 text-sm text-gray-500">Max size: 2MB</span>
                </div>
              )}
            </div>
          </div>

          {/* PDF Upload */}
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PDF Attachment
            </label>
            <div className="mt-1 flex items-center">
              {pdfPreview ? (
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowPdfViewer(true)}
                    className="px-3 py-2 bg-blue-50 text-blue-700 rounded-md flex items-center hover:bg-blue-100"
                  >
                    <FileUp size={18} className="mr-2" />
                    View PDF
                  </button>
                  <button
                    type="button"
                    onClick={removePdf}
                    className="p-2 text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ) : (
                <div className="flex w-full items-center">
                  <label className="flex flex-col items-center px-4 py-2 bg-white text-blue-600 rounded-lg border border-blue-600 cursor-pointer hover:bg-blue-50">
                    <FileUp size={18} className="mb-1" />
                    <span className="text-sm">Upload PDF</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="application/pdf"
                      onChange={handlePdfChange}
                    />
                  </label>
                  <span className="ml-3 text-sm text-gray-500">Max size: 5MB</span>
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="col-span-2">
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <div className={`admin-quill-wrapper border rounded-md overflow-hidden bg-white ${errors.content ? 'border-red-500' : 'border-gray-300'}`}>
              <ReactQuill
                value={formData.content}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your advertisement content here..."
              />
            </div>
            {errors.content && (
              <p className="mt-1 text-sm text-red-500">{errors.content}</p>
            )}
          </div>
        </div>
          </div>

          <PublishingOptionsPanel>
            <label htmlFor="isActive" className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700 leading-snug">Active (visible on the site)</span>
            </label>
            <label htmlFor="isFeatured" className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="isFeatured"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleInputChange}
                className="mt-0.5 h-4 w-4 shrink-0 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700 leading-snug">Featured</span>
            </label>
          </PublishingOptionsPanel>
        </div>

        {/* Form Actions */}
        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:space-x-3 sm:gap-0">
          <button
            type="button"
            onClick={() => navigate('/super-admin/advertisement-list')}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center"
            disabled={isSubmitting}
          >
            <X size={18} className="mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                {isEditMode ? 'Update Advertisement' : 'Create Advertisement'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* PDF Flipbook Viewer Modal */}
      {showPdfViewer && pdfPreview && (
        <PageFlipViewer
          pdfUrl={pdfPreview}
          title={formData.title || "PDF Document"}
          onClose={() => setShowPdfViewer(false)}
        />
      )}
    </div>
  );
};

export default AddAdvertisementAdminPage;
